import { useState } from 'react';
import { useLocation } from 'wouter';
import { 
  Brain, 
  TrendingUp, 
  Radar, 
  MessageSquare, 
  Users, 
  Percent, 
  Package, 
  AlertTriangle,
  Sparkles,
  BarChart3,
  Zap,
  Target,
  CheckCircle2,
  X,
  ArrowRight,
  Shield,
  Globe,
  Clock,
  Database,
  Activity,
  Gauge,
  LineChart,
  Upload,
  Cpu,
  Lightbulb,
  Store,
  Palette,
  FileText,
  Lock,
  Cloud,
  ShieldCheck,
  UserCheck,
  Gift,
  Star,
  ExternalLink
} from 'lucide-react';

export default function HomePage() {
  const [, navigate] = useLocation();
  const [showBetaModal, setShowBetaModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name) {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');

    try {
      const response = await fetch('/api/beta-signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitSuccess(true);
        setFormData({ name: '', email: '', company: '', phone: '', message: '' });
        setTimeout(() => {
          setShowBetaModal(false);
          setSubmitSuccess(false);
        }, 3000);
      } else {
        setSubmitError(data.error || 'Failed to submit. Please try again.');
      }
    } catch (error) {
      setSubmitError('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const aiFeatures = [
    {
      icon: Brain,
      title: 'AI Copilot',
      description: 'Ask questions in natural language. Get instant insights without writing SQL or complex queries. Powered by proprietary PatternIQ AI.',
      gradient: 'from-purple-500 to-blue-500'
    },
    {
      icon: TrendingUp,
      title: 'AI Sales Forecaster',
      description: 'ML-powered 8-week demand predictions with confidence intervals. Plan inventory and staffing with precision.',
      gradient: 'from-blue-500 to-cyan-500'
    },
    {
      icon: Radar,
      title: 'PatternIQ Radar',
      description: 'Real-time anomaly detection across sales, inventory, and operations. Get alerts before problems escalate.',
      gradient: 'from-cyan-500 to-teal-500'
    },
    {
      icon: MessageSquare,
      title: 'Consumer Pulse AI',
      description: 'Social sentiment analysis from reviews, social media, and forums. Track brand perception in real-time.',
      gradient: 'from-teal-500 to-green-500'
    },
    {
      icon: Users,
      title: 'Store Cluster Intelligence',
      description: 'Automated store segmentation based on performance patterns. Identify high-growth and at-risk locations.',
      gradient: 'from-green-500 to-emerald-500'
    },
    {
      icon: Percent,
      title: 'Promo Optimizer',
      description: 'AI-powered promotion simulator. Calculate ROI, margin impact, and sales lift before running campaigns.',
      gradient: 'from-emerald-500 to-lime-500'
    },
    {
      icon: Package,
      title: 'Replenishment Advisor',
      description: 'Smart inventory recommendations driven by forecasts. Optimize stock levels and reduce carrying costs.',
      gradient: 'from-orange-500 to-red-500'
    },
    {
      icon: AlertTriangle,
      title: 'Stockout Early Warning',
      description: 'Predictive alerts for potential stockouts. Never miss a sale due to inventory gaps.',
      gradient: 'from-red-500 to-pink-500'
    }
  ];

  const valueProps = [
    {
      icon: Zap,
      title: 'Faster Decisions',
      description: 'Real-time insights replace hours of manual analysis'
    },
    {
      icon: Target,
      title: 'Precision Forecasting',
      description: 'ML models learn from your data patterns for accurate predictions'
    },
    {
      icon: BarChart3,
      title: 'Proactive Alerts',
      description: 'Catch issues before they impact revenue'
    },
    {
      icon: Database,
      title: 'All Data in One Place',
      description: 'Unified analytics across sales, inventory, pricing, and sentiment'
    }
  ];

  const trustBadges = [
    { icon: Shield, text: 'Enterprise Security' },
    { icon: Globe, text: 'Cloud-Based Platform' },
    { icon: Clock, text: 'Real-Time Updates' },
    { icon: CheckCircle2, text: 'Purpose-Built for Retail' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50">
      {/* Header/Navigation */}
      <nav className="bg-white/80 backdrop-blur-lg border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center">
              <img src="/logo.svg" alt="PatternIQ" className="h-12 w-auto transition-transform hover:scale-105" />
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/login')}
                className="px-4 py-2 text-gray-700 hover:text-gray-900 font-medium transition-colors"
              >
                Login
              </button>
              <button
                onClick={() => setShowBetaModal(true)}
                className="px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl"
              >
                Request Beta Access
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden pt-20 pb-32">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-cyan-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* Large Logo Display */}
          <div className="mb-8 flex justify-center">
            <div className="relative group">
              <div className="absolute -inset-4 bg-gradient-to-r from-blue-600 via-purple-600 to-blue-600 rounded-2xl blur-2xl opacity-30 group-hover:opacity-50 transition duration-500"></div>
              <div className="relative bg-white/90 backdrop-blur-xl rounded-2xl p-8 shadow-2xl border border-blue-100">
                <img src="/logo.svg" alt="PatternIQ" className="h-20 md:h-24 w-auto" />
              </div>
            </div>
          </div>

          <div className="inline-flex items-center px-4 py-2 bg-blue-100 rounded-full mb-8">
            <Sparkles className="w-4 h-4 text-blue-600 mr-2" />
            <span className="text-sm font-semibold text-blue-900">Proprietary AI for Retail Brands</span>
          </div>

          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
            <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-blue-600 bg-clip-text text-transparent">
              Retail-First Intelligence
            </span>
            <br />
            <span className="text-gray-900">Powered by Proprietary AI</span>
          </h1>

          <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed">
            Purpose-built for CPG brands selling at Ulta, Macy's, Target, and beyond. 
            ML models trained on $500M+ in retail data that predict, alert, and optimize—automatically.
          </p>
          
          <p className="text-lg text-gray-500 mb-12 max-w-2xl mx-auto">
            Predict stockouts 3 weeks in advance • Reduce carrying costs by 25% • Save 20+ hours per week
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <button
              onClick={() => setShowBetaModal(true)}
              className="px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-bold text-lg hover:from-blue-700 hover:to-purple-700 transition-all shadow-2xl hover:shadow-blue-500/50 hover:scale-105 transform flex items-center"
            >
              Get Started
              <ArrowRight className="w-5 h-5 ml-2" />
            </button>
            <button
              onClick={() => navigate('/login')}
              className="px-8 py-4 bg-white text-gray-700 rounded-xl font-bold text-lg hover:bg-gray-50 transition-all shadow-lg border-2 border-gray-200"
            >
              Sign In
            </button>
          </div>

          {/* Trust Badges */}
          <div className="mt-16 flex flex-wrap justify-center gap-8">
            {trustBadges.map((badge, index) => (
              <div key={index} className="flex items-center space-x-2 text-gray-600">
                <badge.icon className="w-5 h-5 text-blue-600" />
                <span className="text-sm font-medium">{badge.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who It's For */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Built for CPG Brands in Retail
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Trusted by beauty, personal care, and consumer goods brands selling across major retailers
            </p>
          </div>

          {/* Retailer Logos */}
          <div className="mb-16 flex flex-wrap justify-center items-center gap-8 md:gap-12">
            <div className="text-gray-400 hover:text-gray-600 transition-colors">
              <Store className="w-12 h-12 md:w-16 md:h-16" />
              <p className="text-sm mt-2 font-medium">Ulta Beauty</p>
            </div>
            <div className="text-gray-400 hover:text-gray-600 transition-colors">
              <Store className="w-12 h-12 md:w-16 md:h-16" />
              <p className="text-sm mt-2 font-medium">Macy's</p>
            </div>
            <div className="text-gray-400 hover:text-gray-600 transition-colors">
              <Store className="w-12 h-12 md:w-16 md:h-16" />
              <p className="text-sm mt-2 font-medium">Target</p>
            </div>
            <div className="text-gray-400 hover:text-gray-600 transition-colors">
              <Store className="w-12 h-12 md:w-16 md:h-16" />
              <p className="text-sm mt-2 font-medium">Nordstrom</p>
            </div>
            <div className="text-gray-400 hover:text-gray-600 transition-colors">
              <Store className="w-12 h-12 md:w-16 md:h-16" />
              <p className="text-sm mt-2 font-medium">Amazon</p>
            </div>
          </div>

          {/* Three Columns */}
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-8 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl border border-blue-100">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                  <Palette className="w-8 h-8 text-white" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Beauty & Personal Care</h3>
              <p className="text-gray-600 mb-4">
                Skincare, cosmetics, hair care, and wellness brands managing complex SKU portfolios
              </p>
              <ul className="text-sm text-gray-500 space-y-2 text-left">
                <li className="flex items-start">
                  <CheckCircle2 className="w-4 h-4 mr-2 mt-0.5 text-blue-600" />
                  <span>Multi-store inventory optimization</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-4 h-4 mr-2 mt-0.5 text-blue-600" />
                  <span>Seasonal demand forecasting</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-4 h-4 mr-2 mt-0.5 text-blue-600" />
                  <span>Promotion impact analysis</span>
                </li>
              </ul>
            </div>

            <div className="text-center p-8 bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl border border-purple-100">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                  <Store className="w-8 h-8 text-white" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Multi-Channel Retailers</h3>
              <p className="text-gray-600 mb-4">
                Brands selling through multiple retail partners with unified data needs
              </p>
              <ul className="text-sm text-gray-500 space-y-2 text-left">
                <li className="flex items-start">
                  <CheckCircle2 className="w-4 h-4 mr-2 mt-0.5 text-purple-600" />
                  <span>Cross-retailer performance tracking</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-4 h-4 mr-2 mt-0.5 text-purple-600" />
                  <span>Channel-specific insights</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-4 h-4 mr-2 mt-0.5 text-purple-600" />
                  <span>Unified reporting dashboard</span>
                </li>
              </ul>
            </div>

            <div className="text-center p-8 bg-gradient-to-br from-orange-50 to-red-50 rounded-2xl border border-orange-100">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">
                  <BarChart3 className="w-8 h-8 text-white" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Data-Driven Teams</h3>
              <p className="text-gray-600 mb-4">
                Operations, analytics, and strategy teams tired of manual Excel reporting
              </p>
              <ul className="text-sm text-gray-500 space-y-2 text-left">
                <li className="flex items-start">
                  <CheckCircle2 className="w-4 h-4 mr-2 mt-0.5 text-orange-600" />
                  <span>Automated weekly reporting</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-4 h-4 mr-2 mt-0.5 text-orange-600" />
                  <span>AI-powered recommendations</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-4 h-4 mr-2 mt-0.5 text-orange-600" />
                  <span>Natural language queries</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Impact Metrics */}
      <section className="py-16 bg-white border-y border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="flex justify-center mb-3">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
                  <Gauge className="w-7 h-7 text-white" />
                </div>
              </div>
              <div className="text-4xl font-bold text-gray-900 mb-1">80%</div>
              <div className="text-sm text-gray-600 font-medium">Faster Decisions</div>
            </div>
            
            <div className="text-center">
              <div className="flex justify-center mb-3">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center">
                  <Clock className="w-7 h-7 text-white" />
                </div>
              </div>
              <div className="text-4xl font-bold text-gray-900 mb-1">20+</div>
              <div className="text-sm text-gray-600 font-medium">Hours Saved/Week</div>
            </div>
            
            <div className="text-center">
              <div className="flex justify-center mb-3">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                  <Activity className="w-7 h-7 text-white" />
                </div>
              </div>
              <div className="text-4xl font-bold text-gray-900 mb-1">95%</div>
              <div className="text-sm text-gray-600 font-medium">Forecast Accuracy</div>
            </div>
            
            <div className="text-center">
              <div className="flex justify-center mb-3">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">
                  <Zap className="w-7 h-7 text-white" />
                </div>
              </div>
              <div className="text-4xl font-bold text-gray-900 mb-1">Real-Time</div>
              <div className="text-sm text-gray-600 font-medium">Instant Alerts</div>
            </div>
          </div>
        </div>
      </section>

      {/* Product Demo / See It In Action */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              See PatternIQ in Action
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Real-time insights, predictive analytics, and automated alerts—all in one intuitive dashboard
            </p>
          </div>

          {/* Dashboard Mockup */}
          <div className="relative max-w-5xl mx-auto">
            <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
              {/* Mockup Header */}
              <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-4 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="flex space-x-2">
                    <div className="w-3 h-3 rounded-full bg-white/30"></div>
                    <div className="w-3 h-3 rounded-full bg-white/30"></div>
                    <div className="w-3 h-3 rounded-full bg-white/30"></div>
                  </div>
                  <span className="text-white font-semibold">PatternIQ Dashboard</span>
                </div>
                <div className="text-white text-sm">Live Demo</div>
              </div>

              {/* Mockup Content */}
              <div className="p-6 space-y-4">
                {/* KPI Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-100">
                    <div className="text-sm text-gray-600 mb-1">Total Revenue</div>
                    <div className="text-2xl font-bold text-gray-900">$1.2M</div>
                    <div className="text-xs text-green-600 flex items-center mt-1">
                      <TrendingUp className="w-3 h-3 mr-1" />
                      +12.5% vs LW
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-4 rounded-lg border border-purple-100">
                    <div className="text-sm text-gray-600 mb-1">Units Sold</div>
                    <div className="text-2xl font-bold text-gray-900">15.2K</div>
                    <div className="text-xs text-green-600 flex items-center mt-1">
                      <TrendingUp className="w-3 h-3 mr-1" />
                      +8.3%
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-orange-50 to-red-50 p-4 rounded-lg border border-orange-100">
                    <div className="text-sm text-gray-600 mb-1">Forecast Accuracy</div>
                    <div className="text-2xl font-bold text-gray-900">95%</div>
                    <div className="text-xs text-blue-600 flex items-center mt-1">
                      <Zap className="w-3 h-3 mr-1" />
                      AI Powered
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-4 rounded-lg border border-green-100">
                    <div className="text-sm text-gray-600 mb-1">Active Alerts</div>
                    <div className="text-2xl font-bold text-gray-900">3</div>
                    <div className="text-xs text-orange-600 flex items-center mt-1">
                      <AlertTriangle className="w-3 h-3 mr-1" />
                      Needs Attention
                    </div>
                  </div>
                </div>

                {/* AI Insights Panel */}
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-lg border border-blue-200">
                  <div className="flex items-start space-x-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center flex-shrink-0">
                      <Brain className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold text-gray-900 mb-1 flex items-center">
                        AI Insight
                        <span className="ml-2 px-2 py-0.5 bg-blue-600 text-white text-xs rounded-full">New</span>
                      </div>
                      <p className="text-sm text-gray-700">
                        <strong>Moisturizer SKU-001</strong> is trending 23% above forecast. Consider increasing replenishment orders for next week to avoid stockouts.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Sample Table */}
                <div className="overflow-hidden border border-gray-200 rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Product</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Revenue</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Units</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Forecast</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200 text-sm">
                      <tr>
                        <td className="px-4 py-2 text-gray-900">Serum SKU-002</td>
                        <td className="px-4 py-2 text-gray-700">$45,200</td>
                        <td className="px-4 py-2 text-gray-700">2,400</td>
                        <td className="px-4 py-2"><span className="text-green-600">↑ 15%</span></td>
                      </tr>
                      <tr className="bg-gray-50">
                        <td className="px-4 py-2 text-gray-900">Cleanser SKU-003</td>
                        <td className="px-4 py-2 text-gray-700">$38,100</td>
                        <td className="px-4 py-2 text-gray-700">1,850</td>
                        <td className="px-4 py-2"><span className="text-green-600">↑ 8%</span></td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2 text-gray-900">Toner SKU-004</td>
                        <td className="px-4 py-2 text-gray-700">$29,400</td>
                        <td className="px-4 py-2 text-gray-700">1,620</td>
                        <td className="px-4 py-2"><span className="text-blue-600">→ Stable</span></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Feature Callouts */}
            <div className="absolute -left-4 top-1/4 hidden lg:block">
              <div className="bg-white px-4 py-2 rounded-lg shadow-lg border border-blue-200 flex items-center space-x-2">
                <Radar className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium text-gray-900">Real-time Alerts</span>
              </div>
            </div>
            <div className="absolute -right-4 top-1/3 hidden lg:block">
              <div className="bg-white px-4 py-2 rounded-lg shadow-lg border border-purple-200 flex items-center space-x-2">
                <TrendingUp className="w-4 h-4 text-purple-600" />
                <span className="text-sm font-medium text-gray-900">8-Week ML Forecasts</span>
              </div>
            </div>
            <div className="absolute -left-4 bottom-1/4 hidden lg:block">
              <div className="bg-white px-4 py-2 rounded-lg shadow-lg border border-green-200 flex items-center space-x-2">
                <Brain className="w-4 h-4 text-green-600" />
                <span className="text-sm font-medium text-gray-900">Natural Language Queries</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AI Features Showcase */}
      <section className="py-20 bg-white/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              8 AI Features, One Platform
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Everything you need to run a smarter retail business—powered by proprietary AI
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {aiFeatures.map((feature, index) => (
              <div
                key={index}
                className="group bg-white rounded-2xl p-6 border border-gray-200 hover:border-transparent hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 cursor-pointer"
              >
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${feature.gradient} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <feature.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              How PatternIQ Works
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Get up and running in minutes with our simple, three-step process
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 relative">
            {/* Connection Lines */}
            <div className="hidden md:block absolute top-1/4 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-300 via-purple-300 to-blue-300"></div>
            
            {/* Step 1 */}
            <div className="relative">
              <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-200 hover:shadow-2xl transition-shadow">
                <div className="flex justify-center mb-6">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center relative z-10">
                    <Upload className="w-10 h-10 text-white" />
                  </div>
                </div>
                <div className="text-center mb-4">
                  <div className="inline-block px-4 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-3">
                    Step 1
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Connect Your Data</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Seamlessly integrate with your existing retail systems. No complex setup required.
                  </p>
                </div>
              </div>
            </div>

            {/* Step 2 */}
            <div className="relative">
              <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-200 hover:shadow-2xl transition-shadow">
                <div className="flex justify-center mb-6">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center relative z-10">
                    <Cpu className="w-10 h-10 text-white" />
                  </div>
                </div>
                <div className="text-center mb-4">
                  <div className="inline-block px-4 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-semibold mb-3">
                    Step 2
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">AI Analyzes Patterns</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Our proprietary AI models learn your business patterns and identify opportunities.
                  </p>
                </div>
              </div>
            </div>

            {/* Step 3 */}
            <div className="relative">
              <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-200 hover:shadow-2xl transition-shadow">
                <div className="flex justify-center mb-6">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center relative z-10">
                    <Lightbulb className="w-10 h-10 text-white" />
                  </div>
                </div>
                <div className="text-center mb-4">
                  <div className="inline-block px-4 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-semibold mb-3">
                    Step 3
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Get Actionable Insights</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Receive real-time alerts, forecasts, and recommendations to optimize your business.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Integrations */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Connects to Your Entire Retail Stack
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Seamlessly integrate with the tools and platforms you already use
            </p>
          </div>

          <div className="space-y-12">
            {/* Data Sources */}
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Retail Data Sources</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
                {[
                  { name: 'Ulta Beauty POS', icon: Store },
                  { name: 'Macy\'s Portal', icon: Store },
                  { name: 'Amazon Vendor', icon: Package },
                  { name: 'Target Partners', icon: Store },
                  { name: 'Shopify', icon: Store },
                  { name: 'CSV/Excel', icon: FileText }
                ].map((source, index) => (
                  <div key={index} className="flex flex-col items-center p-4 bg-gray-50 rounded-xl border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all">
                    <source.icon className="w-10 h-10 text-gray-600 mb-2" />
                    <span className="text-sm font-medium text-gray-900 text-center">{source.name}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Marketing & Analytics */}
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Marketing & Analytics</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {[
                  { name: 'Google Ads', icon: TrendingUp },
                  { name: 'Meta Ads', icon: Users },
                  { name: 'TikTok Ads', icon: MessageSquare },
                  { name: 'Google Analytics', icon: BarChart3 }
                ].map((tool, index) => (
                  <div key={index} className="flex flex-col items-center p-4 bg-gray-50 rounded-xl border border-gray-200 hover:border-purple-300 hover:bg-purple-50 transition-all">
                    <tool.icon className="w-10 h-10 text-gray-600 mb-2" />
                    <span className="text-sm font-medium text-gray-900 text-center">{tool.name}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Business Tools */}
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Business Tools</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {[
                  { name: 'QuickBooks', icon: Database },
                  { name: 'Google Sheets', icon: FileText },
                  { name: 'Slack Alerts', icon: MessageSquare },
                  { name: 'Email Reports', icon: FileText }
                ].map((tool, index) => (
                  <div key={index} className="flex flex-col items-center p-4 bg-gray-50 rounded-xl border border-gray-200 hover:border-green-300 hover:bg-green-50 transition-all">
                    <tool.icon className="w-10 h-10 text-gray-600 mb-2" />
                    <span className="text-sm font-medium text-gray-900 text-center">{tool.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Security & Compliance */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Enterprise-Grade Security
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Your data is protected with industry-leading security and compliance standards
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-200 text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                  <Lock className="w-8 h-8 text-white" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">256-bit Encryption</h3>
              <p className="text-gray-600 text-sm">
                All data encrypted at rest and in transit using industry-standard AES-256 encryption
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-200 text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                  <ShieldCheck className="w-8 h-8 text-white" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">SOC 2 Type II</h3>
              <p className="text-gray-600 text-sm">
                Actively pursuing SOC 2 Type II certification for enterprise compliance
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-200 text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center">
                  <Shield className="w-8 h-8 text-white" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">GDPR & CCPA Ready</h3>
              <p className="text-gray-600 text-sm">
                Compliant with global privacy regulations and data protection standards
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-200 text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">
                  <Cloud className="w-8 h-8 text-white" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">AWS Infrastructure</h3>
              <p className="text-gray-600 text-sm">
                Built on Amazon Web Services for 99.99% uptime and global scalability
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-200 text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center">
                  <UserCheck className="w-8 h-8 text-white" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">No PII Storage</h3>
              <p className="text-gray-600 text-sm">
                We never store personally identifiable information - only aggregated business metrics
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-200 text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center">
                  <ShieldCheck className="w-8 h-8 text-white" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Secure Data Processing</h3>
              <p className="text-gray-600 text-sm">
                Regular security audits, penetration testing, and continuous monitoring
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Beta Program Benefits */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-blue-600 rounded-3xl p-8 md:p-12 shadow-2xl">
            <div className="text-center mb-10">
              <div className="inline-block px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full mb-4">
                <span className="text-white font-semibold flex items-center">
                  <Star className="w-4 h-4 mr-2" />
                  Limited Beta Program
                </span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                Why Join the Beta?
              </h2>
              <p className="text-xl text-blue-100">
                Be among the first brands to transform retail operations with PatternIQ AI
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20">
                <div className="flex items-start space-x-3">
                  <CheckCircle2 className="w-6 h-6 text-white flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-lg font-bold text-white mb-1">Exclusive Early Access Pricing</h3>
                    <p className="text-blue-100 text-sm">Lock in beta rates before public launch - save up to 40% off standard pricing</p>
                  </div>
                </div>
              </div>

              <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20">
                <div className="flex items-start space-x-3">
                  <CheckCircle2 className="w-6 h-6 text-white flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-lg font-bold text-white mb-1">White-Glove Onboarding</h3>
                    <p className="text-blue-100 text-sm">Dedicated setup & training with our team - we'll get you up and running in days</p>
                  </div>
                </div>
              </div>

              <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20">
                <div className="flex items-start space-x-3">
                  <CheckCircle2 className="w-6 h-6 text-white flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-lg font-bold text-white mb-1">Priority Feature Requests</h3>
                    <p className="text-blue-100 text-sm">Shape the product roadmap - your feedback drives our development priorities</p>
                  </div>
                </div>
              </div>

              <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20">
                <div className="flex items-start space-x-3">
                  <CheckCircle2 className="w-6 h-6 text-white flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-lg font-bold text-white mb-1">Beta Community Access</h3>
                    <p className="text-blue-100 text-sm">Network with other CPG brands, share insights, and learn best practices</p>
                  </div>
                </div>
              </div>

              <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20">
                <div className="flex items-start space-x-3">
                  <CheckCircle2 className="w-6 h-6 text-white flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-lg font-bold text-white mb-1">Flexible Terms</h3>
                    <p className="text-blue-100 text-sm">No long-term contracts required - test risk-free and cancel anytime</p>
                  </div>
                </div>
              </div>

              <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20">
                <div className="flex items-start space-x-3">
                  <Gift className="w-6 h-6 text-yellow-300 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-lg font-bold text-white mb-1">Beta Bonus: 3 Months Free</h3>
                    <p className="text-blue-100 text-sm">First 50 signups get extended trial - full access to all features at no cost</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center">
              <button
                onClick={() => setShowBetaModal(true)}
                className="px-10 py-4 bg-white text-blue-600 rounded-xl font-bold text-lg hover:bg-gray-100 transition-all shadow-2xl hover:scale-105 transform"
              >
                Request Beta Access Now
              </button>
              <p className="text-blue-100 text-sm mt-4">
                🔥 Only 23 spots remaining in this cohort
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Why PatternIQ Outperforms Traditional BI
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Not just dashboards—intelligent automation that drives business outcomes
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            {valueProps.map((prop, index) => (
              <div
                key={index}
                className="flex items-start space-x-4 bg-white rounded-xl p-6 shadow-lg border border-gray-100"
              >
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
                    <prop.icon className="w-6 h-6 text-white" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{prop.title}</h3>
                  <p className="text-gray-600">{prop.description}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Comparison */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 md:p-12 text-white shadow-2xl">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-2xl font-bold mb-4">Traditional BI Tools</h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <X className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5 opacity-70" />
                    <span>Static dashboards require manual analysis</span>
                  </li>
                  <li className="flex items-start">
                    <X className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5 opacity-70" />
                    <span>Reactive reporting after problems occur</span>
                  </li>
                  <li className="flex items-start">
                    <X className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5 opacity-70" />
                    <span>Generic solutions need customization</span>
                  </li>
                  <li className="flex items-start">
                    <X className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5 opacity-70" />
                    <span>Data silos across multiple platforms</span>
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-2xl font-bold mb-4">PatternIQ AI</h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <CheckCircle2 className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
                    <span>AI-powered insights in natural language</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Predictive alerts before issues impact revenue</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Purpose-built for retail—no setup needed</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Unified platform for all retail intelligence</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 via-purple-600 to-blue-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-block px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full mb-6">
            <span className="text-white font-semibold">Join 100+ Brands Already Using PatternIQ</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Transform Your Retail Operations?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Limited beta spots available - get exclusive early access pricing and dedicated onboarding
          </p>
          <button
            onClick={() => setShowBetaModal(true)}
            className="px-8 py-4 bg-white text-blue-600 rounded-xl font-bold text-lg hover:bg-gray-100 transition-all shadow-2xl hover:scale-105 transform inline-flex items-center"
          >
            Request Beta Access
            <ArrowRight className="w-5 h-5 ml-2" />
          </button>
          <p className="text-blue-100 text-sm mt-4">
            ⚡ Setup in days, not months • 🎁 First 50 signups get 3 months free
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            {/* Company Info */}
            <div className="md:col-span-2">
              <img src="/logo.svg" alt="PatternIQ" className="h-10 w-auto mb-4 brightness-0 invert" />
              <p className="text-gray-400 mb-6 leading-relaxed max-w-md">
                Purpose-built AI for retail brands. Transform your data into actionable intelligence 
                with machine learning models that predict, alert, and optimize—automatically.
              </p>
              <div className="flex space-x-4">
                <div className="flex items-center space-x-2 text-gray-400">
                  <Shield className="w-4 h-4" />
                  <span className="text-sm">Enterprise Grade</span>
                </div>
                <div className="flex items-center space-x-2 text-gray-400">
                  <Globe className="w-4 h-4" />
                  <span className="text-sm">Cloud Platform</span>
                </div>
              </div>
            </div>

            {/* Product */}
            <div>
              <h3 className="text-white font-semibold mb-4">Product</h3>
              <ul className="space-y-3">
                <li>
                  <button 
                    onClick={() => setShowBetaModal(true)}
                    className="text-gray-400 hover:text-white transition-colors text-sm"
                  >
                    AI Features
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => setShowBetaModal(true)}
                    className="text-gray-400 hover:text-white transition-colors text-sm"
                  >
                    Pricing
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => setShowBetaModal(true)}
                    className="text-gray-400 hover:text-white transition-colors text-sm"
                  >
                    Request Demo
                  </button>
                </li>
              </ul>
            </div>

            {/* Company */}
            <div>
              <h3 className="text-white font-semibold mb-4">Company</h3>
              <ul className="space-y-3">
                <li>
                  <button
                    onClick={() => navigate('/login')}
                    className="text-gray-400 hover:text-white transition-colors text-sm"
                  >
                    Login
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => setShowBetaModal(true)}
                    className="text-gray-400 hover:text-white transition-colors text-sm"
                  >
                    Join Beta
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => setShowBetaModal(true)}
                    className="text-gray-400 hover:text-white transition-colors text-sm"
                  >
                    Contact Us
                  </button>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="pt-8 border-t border-gray-800">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <p className="text-gray-400 text-sm mb-4 md:mb-0">
                © 2025 PatternIQ AI. All rights reserved. Proprietary machine learning technology.
              </p>
              <div className="flex space-x-6 text-sm text-gray-400">
                <button 
                  onClick={() => setShowBetaModal(true)}
                  className="hover:text-white transition-colors"
                >
                  Privacy Policy
                </button>
                <button 
                  onClick={() => setShowBetaModal(true)}
                  className="hover:text-white transition-colors"
                >
                  Terms of Service
                </button>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Beta Signup Modal */}
      {showBetaModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={(e) => {
          if (e.target === e.currentTarget) setShowBetaModal(false);
        }}>
          <div className="bg-white rounded-2xl max-w-lg w-full p-8 shadow-2xl relative max-h-[90vh] overflow-y-auto" data-form-type="other">
            <button
              onClick={() => setShowBetaModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>

            {submitSuccess ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h3>
                <p className="text-gray-600">
                  We've received your request and will be in touch soon.
                </p>
              </div>
            ) : (
              <>
                <div className="mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Join the Beta Program</h3>
                  <p className="text-gray-600">
                    Get early access to PatternIQ AI and transform how you run your retail business
                  </p>
                </div>

                {submitError && (
                  <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-700">{submitError}</p>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4" autoComplete="on" data-form-type="beta-signup">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      autoComplete="name"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="John Smith"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      autoComplete="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="john@company.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">
                      Company Name *
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      autoComplete="organization"
                      required
                      value={formData.company}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Acme Corp"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number (Optional)
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      autoComplete="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                      Tell us about your needs (Optional)
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      value={formData.message}
                      onChange={handleInputChange}
                      autoComplete="off"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                      placeholder="What challenges are you facing? How many stores do you operate?"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Submitting...' : 'Request Beta Access'}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

